import React from 'react';
import {
  VictoryChart,
  VictoryArea,
  VictoryAxis,
  VictoryTheme,
  VictoryTooltip,
  VictoryLegend
} from 'victory';
import { formatPercentageES, formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

const COLORS = REPORT_CHART_COLORS;

/**
 * Gráfico 13: Eficiencia y Gestión
 *
 * Area chart showing efficiency indicators (EG1, EG2, EG4)
 */

export interface EfficiencyIndicatorData {
  period: string;
  EG1?: number;  // Absorption Ratio
  EG2?: number;  // Operating Expenses / Average Total Assets
  EG4?: number;  // Operating Expenses / Financial Margin
}

export interface Grafico13Props {
  data: EfficiencyIndicatorData[];
  title?: string;
}

const INDICATOR_COLORS = {
  EG1: COLORS.secondary,  // Teal
  EG2: COLORS.primary,    // Dark gray
  EG4: '#9e9e9e',         // Medium gray
};

const INDICATOR_NAMES = {
  EG1: 'Grado de Absorción',
  EG2: 'Gastos Op./Activo Total',
  EG4: 'Gastos Op./Margen Financiero',
};

export const Grafico13: React.FC<Grafico13Props> = ({
  data,
  title = 'Gráfico 13: Eficiencia y Gestión'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Transform data for each indicator
  const indicators = ['EG1', 'EG2', 'EG4'] as const;
  const chartDatasets = indicators.map(indicator => ({
    indicator,
    name: INDICATOR_NAMES[indicator],
    color: INDICATOR_COLORS[indicator],
    data: sortedData
      .filter(d => d[indicator] !== undefined && d[indicator] !== null)
      .map(d => ({
        x: formatPeriodES(d.period),
        y: d[indicator]! * 100,
        // Store metadata for tooltip
        indicatorName: INDICATOR_NAMES[indicator],
        period: formatPeriodES(d.period),
        value: formatPercentageES(d[indicator]! * 100)
      }))
  })).filter(ds => ds.data.length > 0);

  if (chartDatasets.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos de eficiencia disponibles</p>
      </div>
    );
  }

  // Calculate global y-axis domain
  const allValues = chartDatasets.flatMap(ds => ds.data.map(d => d.y));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const padding = (maxValue - minValue) * 0.1;
  const yDomain: [number, number] = [
    Math.max(0, minValue - padding),
    maxValue + padding
  ];

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 20 }}
          padding={{ top: 40, bottom: 60, left: 60, right: 20 }}
        >
          {/* Legend */}
          <VictoryLegend
            x={70}
            y={0}
            orientation="horizontal"
            gutter={15}
            style={{ labels: { fontSize: 9 } }}
            data={chartDatasets.map(ds => ({
              name: ds.name,
              symbol: { fill: ds.color }
            }))}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Y-Axis */}
          <VictoryAxis
            dependentAxis
            domain={yDomain}
            tickFormat={(t: number) => `${t.toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Area charts for each indicator */}
          {chartDatasets.map(ds => (
            <VictoryArea
              key={ds.indicator}
              data={ds.data}
              style={{
                data: {
                  fill: ds.color,
                  fillOpacity: 0.3,
                  stroke: ds.color,
                  strokeWidth: 2
                }
              }}
              labels={({ datum }) => `${datum.indicatorName}\n${datum.period}: ${datum.value}`}
              labelComponent={
                <VictoryTooltip
                  style={{ fontSize: 9 }}
                  flyoutStyle={{
                    fill: 'white',
                    stroke: ds.color,
                    strokeWidth: 1
                  }}
                  cornerRadius={4}
                  pointerLength={6}
                />
              }
            />
          ))}
        </VictoryChart>
      </div>

      <p className="chart-description">
        Los indicadores de eficiencia miden la capacidad de la institución para controlar gastos.
        Valores más bajos indican mejor eficiencia operativa.
      </p>

      <p className="chart-source">
        Fuente: Estados financieros de la institución
      </p>
    </div>
  );
};

export default Grafico13;
